import { useLayoutEffect, useState } from 'react';
import clsx from 'clsx';

/**
 * Animates its children in and out with a vertical “grow” effect using a CSS grid
 * row transition (`0fr` → `1fr`). When `show` becomes false, the collapse runs first;
 * children are unmounted after the transition completes so layout stays accurate.
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Content whose height is animated.
 * @param {boolean} props.show - When true, expands to full height; when false, collapses then unmounts.
 */
export function OrderMinProgressReveal({ children, show }) {
    const [expanded, setExpanded] = useState(false);
    const [isVisible, setIsVisible] = useState(show);

    useLayoutEffect(() => {
        if (show) {
            setIsVisible(true);
            const id = requestAnimationFrame(() => {
                requestAnimationFrame(() => setExpanded(true));
            });
            return () => cancelAnimationFrame(id);
        }
        setExpanded(false);
        return undefined;
    }, [show]);

    const handleTransitionEnd = (event) => {
        if (event.propertyName !== 'grid-template-rows') return;
        if (!show) {
            setIsVisible(false);
        }
    };

    if (!show && !isVisible) {
        return null;
    }

    return (
        <div
            className={clsx(
                'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:duration-150',
                expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
            onTransitionEnd={handleTransitionEnd}
        >
            <div className="min-h-0 overflow-hidden">{children}</div>
        </div>
    );
}
